import type { DashboardMonthlySales } from './dashboardMonthlySales';
import type { CrmWeeklySummaryReport, WeeklyNarrative } from './crmWeeklySummary';

export type CrmWeeklyAiNarrative = WeeklyNarrative & {
  activityBrief: string;
  salespersonBrief: string;
  source: 'gemini' | 'fallback' | 'local';
  geminiError?: string;
};

export type WeeklyAiAudience = {
  mode: 'individual' | 'team';
  label: string;
};

/** Local narrative (no Gemini) — full words, no abbreviations. */
export function buildLocalWeeklyNarrative(report: CrmWeeklySummaryReport): CrmWeeklyAiNarrative {
  return {
    ...report.narrative,
    activityBrief:
      report.dutyFollowUps.withDuty > 0
        ? `Assigned duties in visit descriptions: ${report.dutyFollowUps.followedUp} followed up later, ${report.dutyFollowUps.openCount} still open.`
        : report.themes.length > 0
          ? `From visit descriptions: ${report.themes
              .slice(0, 5)
              .map((t) => `${t.theme} (${t.count})`)
              .join('; ')}.`
          : report.visitCount === 0
            ? 'No visits this week.'
            : 'Open the visit list below to read individual descriptions.',
    salespersonBrief:
      report.byOwner.length === 0
        ? 'No salesperson activity this week.'
        : report.byOwner
            .slice(0, 10)
            .map((o) => {
              const nv = `New Visit ${o.newVisitUnique}/${o.newVisitTarget}${o.newVisitMet ? ' met' : ` (${o.newVisitRemaining} short)`}`;
              const daily = `daily target ${o.workdaysMet}/${o.workdayCount} weekdays`;
              return `${o.ownerLabel}: ${o.visitCount} visit${o.visitCount === 1 ? '' : 's'}, ${nv}, ${daily}`;
            })
            .join('; ') + '.',
    source: 'local',
  };
}

function compactSales(sales: DashboardMonthlySales | null | undefined) {
  if (!sales) return null;
  const thisYtd = sales.months.reduce((s, m) => s + m.this_year, 0);
  const lastYtd = sales.months.reduce((s, m) => s + m.last_year, 0);
  const yoy =
    lastYtd === 0 ? null : Math.round(((thisYtd - lastYtd) / Math.abs(lastYtd)) * 1000) / 10;
  return {
    year: sales.year,
    last_year: sales.last_year,
    thisYearToDate: thisYtd,
    lastYearToDate: lastYtd,
    yearOverYearPercent: yoy,
    months: sales.months.map((m) => ({
      month: m.month,
      this_year: m.this_year,
      last_year: m.last_year,
    })),
  };
}

export function buildWeeklyAiRequestBody(
  report: CrmWeeklySummaryReport,
  audience: WeeklyAiAudience,
  sales?: DashboardMonthlySales | null,
) {
  const samples = report.visits
    .filter((v) => v.hasDescription)
    .slice(0, 40)
    .map((v) => ({
      dayLabel: v.dayLabel,
      ownerLabel: v.ownerLabel,
      companyName: v.companyName,
      title: v.title,
      description: v.description.slice(0, 240),
    }));

  return {
    weekLabel: report.weekLabel,
    audienceMode: audience.mode,
    audienceLabel: audience.label,
    localNarrative: report.narrative,
    visitCount: report.visitCount,
    accountCount: report.accountCount,
    notVisitedCount: report.notVisitedCount,
    titleSlices: report.titleSlices.map((s) => ({
      label: s.label,
      count: s.count,
      pct: s.pct,
    })),
    targetRules: {
      newVisitTargetPerStaff: report.newVisitsMonth.target,
      dailyVisitTargetPerStaff: report.dailyTarget,
      note: 'Targets are personal per salesperson. Do not pool or sum across the team. One person exceeding target does not cover anyone else.',
    },
    isTeamScope: report.isTeamScope,
    staffTargetProgress: report.isTeamScope
      ? {
          staffHittingNewVisit10: report.newVisitsMonth.staffMet,
          staffMeetingDaily8EveryWeekday: report.staffDailyTargetMet,
          staffCount: report.staffCount,
        }
      : null,
    byOwner: report.byOwner.map((o) => {
      const titleCounts = new Map<string, number>();
      for (const v of report.visits) {
        if ((v.ownerId || v.ownerLabel) !== (o.ownerId || o.ownerLabel)) continue;
        const t = v.title || 'Others';
        titleCounts.set(t, (titleCounts.get(t) ?? 0) + 1);
      }
      const topTitles = [...titleCounts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([label, count]) => `${label} (${count})`);
      return {
        ownerLabel: o.ownerLabel,
        visitCount: o.visitCount,
        accountCount: o.accountCount,
        newVisitUnique: o.newVisitUnique,
        newVisitTarget: o.newVisitTarget,
        newVisitMet: o.newVisitMet,
        newVisitRemaining: o.newVisitRemaining,
        workdaysMetDailyTarget: o.workdaysMet,
        workdayCount: o.workdayCount,
        dailyTargetMet: o.dailyTargetMet,
        topTitles,
      };
    }),
    projects: {
      total: report.projects.total,
      ongoing: report.projects.ongoing,
      onHold: report.projects.onHold,
      completed: report.projects.completed,
      problemProjects: report.projects.problemProjects.slice(0, 8),
    },
    projectUpdateHealth: {
      ongoingTotal: report.projectUpdateHealth.ongoingTotal,
      onTime: report.projectUpdateHealth.onTime,
      stale: report.projectUpdateHealth.stale,
      neverUpdated: report.projectUpdateHealth.neverUpdated,
      staleProjects: report.projectUpdateHealth.staleProjects.slice(0, 8),
    },
    newVisitsMonth: report.newVisitsMonth,
    dailyTarget: report.dailyTarget,
    workdaysMet: report.workdaysMet,
    workdayCount: report.workdayCount,
    byDay: report.byDay.map((d) => ({
      day: d.weekdayShort,
      visitCount: d.visitCount,
      isWorkday: d.isWorkday,
      metDailyTarget: d.metDailyTarget,
    })),
    highFrequency: report.highFrequency.slice(0, 12),
    dutyFollowUps: {
      withDuty: report.dutyFollowUps.withDuty,
      followedUp: report.dutyFollowUps.followedUp,
      openCount: report.dutyFollowUps.openCount,
      openItems: report.dutyFollowUps.items.filter((i) => !i.followedUp).slice(0, 10),
    },
    sales: compactSales(sales),
    visitSamples: samples,
  };
}

/**
 * Call portal PHP proxy (same GEMINI_API_KEY as PO extract / DEM integrations).
 * Edge Function is not used — Supabase secrets do not include the Gemini key.
 */
export async function fetchCrmWeeklyAiNarrative(
  report: CrmWeeklySummaryReport,
  audience: WeeklyAiAudience,
  sales?: DashboardMonthlySales | null,
): Promise<CrmWeeklyAiNarrative> {
  const local = buildLocalWeeklyNarrative(report);

  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    return { ...local, geminiError: 'VITE_DEM_EMAIL_PROXY_KEY is not configured.' };
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-DEM-EMAIL-KEY': proxyKey,
    };

    const response = await fetch('/crm-weekly-summary-api', {
      method: 'POST',
      headers,
      body: JSON.stringify(buildWeeklyAiRequestBody(report, audience, sales)),
    });

    const payload = (await response.json().catch(() => null)) as
      | (Partial<CrmWeeklyAiNarrative> & { error?: string; geminiError?: string })
      | null;

    if (!response.ok || !payload || payload.error) {
      return {
        ...local,
        source: 'fallback',
        geminiError: payload?.error || `Proxy failed (${response.status}).`,
      };
    }
    if (!payload.summary?.trim()) {
      return {
        ...local,
        source: 'fallback',
        geminiError: payload.geminiError || 'Empty Gemini summary.',
      };
    }

    return {
      summary: String(payload.summary).trim(),
      recommendation: String(payload.recommendation ?? local.recommendation).trim(),
      findings: Array.isArray(payload.findings)
        ? payload.findings.map((f) => String(f).trim()).filter(Boolean)
        : local.findings,
      activityBrief: String(payload.activityBrief ?? local.activityBrief).trim(),
      salespersonBrief: String(payload.salespersonBrief ?? local.salespersonBrief).trim(),
      source: payload.source === 'gemini' ? 'gemini' : 'fallback',
      geminiError: payload.geminiError,
    };
  } catch (err) {
    console.warn('[crm-weekly-summary]', err);
    return {
      ...local,
      source: 'fallback',
      geminiError: err instanceof Error ? err.message : 'Network error',
    };
  }
}
